import { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import { Login, Signup, ForgotPassword } from './pages/Auth';
import { Toaster } from './components/ui/toaster';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute, OnboardingRoute } from './components/routes';
import { Loader2 } from 'lucide-react';

// Lazy-load heavy authenticated pages for faster first paint
const About       = lazy(() => import('./pages/About'));
const Help        = lazy(() => import('./pages/Help'));
const Privacy     = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Privacy })));
const Terms       = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Terms })));
const RefundPolicy = lazy(() => import('./pages/Legal').then((m) => ({ default: m.RefundPolicy })));
const Contact     = lazy(() => import('./pages/Legal').then((m) => ({ default: m.Contact })));
const Onboarding  = lazy(() => import('./pages/Onboarding'));
const Dashboard   = lazy(() => import('./pages/Dashboard'));
const Challenge   = lazy(() => import('./pages/Challenge'));
const Practice    = lazy(() => import('./pages/Practice'));
const Progress    = lazy(() => import('./pages/Progress'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Profile     = lazy(() => import('./pages/Profile'));
const Settings    = lazy(() => import('./pages/Settings'));
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  return null;
};

const ChoosePlan  = lazy(() => import('./pages/ChoosePlan'));
const NotFound    = lazy(() => import('./pages/NotFound'));

const Fallback = () => (
  <div className="min-h-screen grid place-items-center bg-[#2A1810]">
    <Loader2 className="animate-spin text-[#F4C430]" size={28} />
  </div>
);

function App() {
  useEffect(() => {
    document.title = 'The Brain Theory — Redefining Screentime';
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
        <ScrollToTop />
          <Suspense fallback={<Fallback />}>
            <Routes>
              {/* Public */}
              <Route path="/"      element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/help"  element={<Help />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms"   element={<Terms />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/contact" element={<Contact />} />

              {/* Auth */}
              <Route path="/auth/login"  element={<Login />} />
              <Route path="/auth/signup" element={<Signup />} />
              <Route path="/auth/forgot" element={<ForgotPassword />} />

              {/* Onboarding */}
              <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />

              {/* Post-login app */}
              <Route path="/dashboard"   element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/challenge"   element={<ProtectedRoute><Challenge /></ProtectedRoute>} />
              <Route path="/practice"    element={<ProtectedRoute><Practice /></ProtectedRoute>} />
              <Route path="/progress"    element={<ProtectedRoute><Progress /></ProtectedRoute>} />
              <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
              <Route path="/choose-plan" element={<ProtectedRoute><ChoosePlan /></ProtectedRoute>} />
              <Route path="/profile"     element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/settings/*"  element={<ProtectedRoute><Settings /></ProtectedRoute>} />

              {/* 404 catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
